import React, { useEffect, useMemo, useState } from "react";
import SideBar from "./SideBar";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../../Util/Loading";
import { setAuthModalOpen } from "store/features/authModalSlice";
import OrderTable from "./OrderTable";
import orderApi from "api/order/OrderApi";
import { motion } from "framer-motion";
import { fadeUp } from "animations/variants";
import { MdAssignment, MdCancel, MdAccessTime } from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";

function OrderList() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user || {});

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  /* ================= STATUS LOGIC (MATCH TABLE) ================= */

  const getOrderStatus = (order) => {
    const now = new Date();
    const created = new Date(order?.createdAt);

    if (order?.orderStatus === "cancelled") return "cancelled";

    const hours = (now - created) / (1000 * 60 * 60);

    if (hours > 24) return "completed";

    return "active";
  };

  /* ================= STATS ================= */

  const stats = useMemo(() => {
    let total = orders.length;
    let active = 0;
    let completed = 0;
    let cancelled = 0;

    orders.forEach((o) => {
      const status = getOrderStatus(o);

      if (status === "cancelled") cancelled++;
      else if (status === "completed") completed++;
      else active++;
    });

    return { total, active, completed, cancelled };
  }, [orders]);

  /* ================= CARDS ================= */

  const cards = [
    {
      title: "Total",
      total: stats.total,
      icon: MdAssignment,
      styles: {
        iconBg: "bg-gray-300",
        iconColor: "text-gray-700",
        border: "border-gray-300",
      },
    },
    {
      title: "Active",
      total: stats.active,
      icon: MdAccessTime,
      styles: {
        iconBg: "bg-neonYellow/20",
        iconColor: "text-neonYellow",
        border: "border-neonYellow",
      },
    },
    {
      title: "Cancelled",
      total: stats.cancelled,
      icon: MdCancel,
      styles: {
        iconBg: "bg-red-100",
        iconColor: "text-red-500",
        border: "border-red-300",
      },
    },
    {
      title: "Completed",
      total: stats.completed,
      icon: AiOutlineCheckCircle,
      styles: {
        iconBg: "bg-green-100",
        iconColor: "text-green-600",
        border: "border-green-300",
      },
    },
  ];

  /* ================= API ================= */

  const getUserOrders = async () => {
    try {
      const { response, err } = await orderApi.getUserOrders();

      if (response?.data?.orders) {
        setOrders(response.data.orders);
      } else {
        setOrders([]);
      }

      if (err) {
        toast.error(err.message || "Failed to load orders");
      }
    } catch (error) {
      toast.error("Something went wrong");
      console.error(error);
    }
  };

  /* ================= EFFECT ================= */

  useEffect(() => {
    if (!user) return;

    const fetch = async () => {
      setIsLoading(true);
      await getUserOrders();
      setIsLoading(false);
    };

    fetch();
  }, [user]);

  useEffect(() => {
    if (!user) dispatch(setAuthModalOpen(true));
  }, [user, dispatch]);

  /* ================= RENDER ================= */

  if (!user) return null;
  if (isLoading) return <Loading />;

  return (
    <SideBar>
      <div className="flex flex-col gap-6">
        {/* HEADER */}
        <div className="flex justify-between items-center">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-heading text-neonYellow animate-neonFlicker my-6 uppercase"
          >
            Order History
          </motion.h2>

          {orders.length > 0 && (
            <button className="bg-neonYellow text-navy px-4 py-2 rounded-md text-sm hover:bg-dry hover:text-white">
              Delete All
            </button>
          )}
        </div>

        {/* STATS */}
        <div className="grid lg:grid-cols-4 sm:grid-cols-2 gap-5">
          {cards.map((card, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              transition={{ delay: i * 0.1 }}
              className={`rounded-lg border-2 ${card.styles.border} p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-all duration-300`}
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${card.styles.iconBg}`}
              >
                <card.icon className={`text-lg ${card.styles.iconColor}`} />
              </div>

              <div>
                <p className="text-sm text-gray-500">{card.title}</p>
                <p className="text-xl font-bold text-gray-800">{card.total}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* TABLE */}
        {orders.length === 0 ? (
          <div className="h-96 flex items-center justify-center text-gray-400">
            No orders found
          </div>
        ) : (
          <OrderTable datas={orders} />
        )}
      </div>
    </SideBar>
  );
}

export default OrderList;
